<script setup lang="ts">
import type { ContentItem } from "@edco/apollo";
import type { IApiMediaWithCrops } from "~/models/generated/IApiMediaWithCrops";

interface BannerContent {
  enableBanner?: boolean;
  headingBackground?: IApiMediaWithCrops[] | null;
  headingBackgroundImagePosition?: string;
  headingContentWidthRatio?: string;
  headingForegroundImagePosition?: string;
  headingForegroundImage?: IApiMediaWithCrops[] | null;
  logos?: IApiMediaWithCrops[] | null;
  heading?: string;
  pageIntro?: string;
  name?: string;
}

const props = defineProps<{
  content: ContentItem<BannerContent>;
}>();

const isRightImage = computed( () => {
  return props.content.properties.headingForegroundImagePosition?.toLowerCase() === "right";
} );

const cols = computed( () => {
  const result: number[] = [];

  if ( props.content.properties.headingContentWidthRatio ) {
    const [numerator, denominator] = props.content.properties.headingContentWidthRatio.split( ":" ).map( Number );

    if ( numerator === 1 && denominator === 0 ) {
      result.push( 12 );
    } else if ( numerator === 1 && denominator === 1 ) {
      result.push( 6, 6 );
    } else if ( numerator === 1 && denominator === 2 ) {
      result.push( 4, 8 );
    } else if ( numerator === 2 && denominator === 1 ) {
      result.push( 8, 4 );
    } else if ( numerator === 3 && denominator === 1 ) {
      result.push( 9, 3 );
    }
  }

  return result;
} );

const imageCols = computed( () => {
  const defaultCols = props.content.properties.headingForegroundImage ? 6 : 12;
  return cols.value.length > 1 ? cols.value[1] : defaultCols;
} );

const contentCols = computed( () => {
  const defaultCols = props.content.properties.headingForegroundImage ? 6 : 12;
  return cols.value.length > 1 ? cols.value[0] : defaultCols;
} );

const headerBackgroundStyle = computed( () => {
  const positionMap: Record<string, string> = {
    Left: "left",
    Right: "right",
    Center: "center",
  };

  const style: Record<string, string> = {};

  if ( props.content.properties.headingBackground?.length ) {
    style.backgroundImage = `url(${props.content.properties.headingBackground[0].url})`;
    style.backgroundRepeat = "no-repeat";

    if ( props.content.properties.headingBackgroundImagePosition === "Full width" ) {
      style.backgroundSize = "cover";
      style.backgroundPosition = "center";
    } else if ( props.content.properties.headingBackgroundImagePosition ) {
      style.backgroundSize = "auto";
      style.backgroundPosition = positionMap[props.content.properties.headingBackgroundImagePosition];
    }
  }

  return style;
} );
</script>

<template>
  <div
    class="dynamic-banner"
    :style="headerBackgroundStyle"
    style="background-position: right 10% bottom 0px;">
    <div class="container">
      <section ref="compHeaderSection">
        <div class="row">
          <div :class="['col-lg-' + contentCols, isRightImage ? 'order-lg-0' : 'order-lg-1']">
            <div class="dynamic-banner__header">
              <div
                v-if="content.properties.logos?.length"
                class="d-flex flex-wrap align-items-center">
                <NuxtImg
                  v-for="logo in content.properties.logos"
                  :key="logo.name"
                  :src="logo.url"
                  :alt="logo.name"
                  class="dynamic-banner__logo" />
              </div>

              <h1 class="dynamic-banner__heading">
                <span class="highlight">
                  {{ content.properties.heading || content.name }}
                </span>
              </h1>
              <sanitize-html
                class="dynamic-banner__intro"
                :dirty="content.properties.pageIntro" />
              <slot />
            </div>
          </div>

          <div
            v-if="content.properties.headingForegroundImage?.length"
            :class="['col-lg-' + imageCols, isRightImage ? 'order-lg-1' : 'order-lg-0']">
            <NuxtImg
              :src="content.properties.headingForegroundImage[0].url"
              :alt="content.properties.headingForegroundImage[0].name"
              class="dynamic-banner__fg-img" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "~/assets/styles/_variables.scss";

.dynamic-banner {
  position: relative;
  // background-color: var(--color-accent-3);
  background-color: #62BBAD;
  background-size: auto 100%!important;
  // background-position: right 25% bottom -50px;
  padding: 120px 0;
  margin-bottom: 40px;

  &__logo {
    width: auto;
    height: 3rem;

    :deep(img) {
      width: auto;
      height: 3rem;
    }
  }

  &__fg-img {
    display: flex;
    width: 100%;

    :deep(img) {
      width: 100%;
    }

    @media (max-width: 991px) {
      margin-top: 30px;
    }
  }

  &__bg {
    position: relative;
    background-repeat: no-repeat;

    @media (max-width: 991px) {
      background-image: unset !important;
    }
  }

  @media (max-width: 991px) {
    max-width: 100%;
      background-image: unset !important;

  }

  &__heading {
    font-size: 2.5rem;
    font-weight: 900;
    margin-top: 2rem;
    color: var(--color-accent-3);

    $correction: 0px;
    margin-left: $correction;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .highlight {
      // background: var(--color-accent-1);
      // color: var(--color-accent-3);
      // box-shadow: $correction 0 0 var(--color-accent-1), -$correction 0 0 var(--color-accent-1);
      // line-height: 3.5rem;
      // box-decoration-break: clone;
      color: #fff;
      font-size: 3rem;
      padding:0;
      margin: 0;
      font-weight: 500;
    }
  }

  &__intro {
    color: $white;
    max-width: 850px;

    @media (max-width: 991px) {
      max-width: 100%;
    }

    :deep(b),
    :deep(strong) {
      font-size: 1rem;
      font-weight: 600;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      color: #fff;
    }
  }
   @media (max-width: 1400px) {
    // background-position: center;
    background-position: right 0 bottom 0 !important;
    background-size: 50% !important;
    height: 300px !important;
    padding-top: 50px;
  }
}
</style>
